// Unit-grouped Trend panels (story #1191; kind → type → signal picker, story #1220).
// A panel is a shared unit/axis; a line is a series — per-instance, capped, scope-aware (not a
// per-building representative). Same-unit overlay is the default; panel-per-name is the one-name
// degenerate case (and the optional split of a shared panel), not a mode.
//
// DEFAULT_COMPARE_BUILDING_COUNT is a UI readability cap so a kanta-scope
// overlay does not plot every building at once — not equipment-domain knowledge,
// so it does not belong in the KB.

export const DEFAULT_COMPARE_BUILDING_COUNT = 4;
export const MAX_COMPARE_BUILDING_COUNT = 6;

export type TrendKind = "talotekniikka" | "vyohykkeet";

export interface TrendSeries {
  series_key: string;
  building_id: string;
  building_name: string;
  source_id: string;
  source_name: string;
  name: string;
  unit: string;
}

export interface SeriesSource {
  source_id: string;
  source_type: "zone" | "equipment" | "building";
  source_name: string;
  available_names: { name: string; unit: string | null }[];
  equipment_type?: string | null;
}

/** One pill in the Trendi "Type" step — an equipment_type bucket (Talotekniikka) or a single
 * zone instance (Vyöhykkeet, which has no zone_type dimension on DataPointSource). */
export interface TypeGroup {
  key: string;
  label: string;
  source_ids: string[];
}

const SOURCE_TYPE_FOR_KIND: Record<TrendKind, SeriesSource["source_type"]> = {
  talotekniikka: "equipment",
  vyohykkeet: "zone",
};

/** Normalises an `equipment_type` slug the same way the KB loader does
 * (`agent_domain/knowledge_base/models.py` `_normalise_type`): lowercase, underscores/spaces →
 * hyphens. Stored `DataPointSource.equipment_type` values are migrating from snake_case to the
 * KB's canonical kebab-case id (#1241/#1242); until every row is backfilled, one kind of machine
 * can show up spelled either way. Normalising before grouping/lookup collapses both spellings
 * into the same Tyyppi chip and the same `equipment_type_labels` (KB, kebab-case) entry.
 * TODO(#1241): drop this normalization once every stored `equipment_type` row is backfilled to
 * the KB's kebab-case slug and the raw value can be trusted as the group key directly. */
function normalize_equipment_type_key(equipment_type: string): string {
  return equipment_type.toLowerCase().replace(/[_\s]+/g, "-");
}

/** Groups sources into Trendi's "Type" step pills for the chosen kind. Talotekniikka groups by
 * `equipment_type`; Vyöhykkeet has
 * no `zone_type` field, so each zone instance is its own group, per the spec's default. */
export function type_groups_for_kind<T extends SeriesSource>(
  sources: T[],
  kind: TrendKind,
  equipment_type_labels: Record<
    string,
    { name_fi: string; icon?: string }
  > = {},
): TypeGroup[] {
  const matching = sources.filter(
    (source) => source.source_type === SOURCE_TYPE_FOR_KIND[kind],
  );
  if (kind === "vyohykkeet") {
    return matching.map((source) => ({
      key: source.source_id,
      label: source.source_name,
      source_ids: [source.source_id],
    }));
  }

  const groups = new Map<string, TypeGroup>();
  const order: string[] = [];
  for (const source of matching) {
    const type_key = source.equipment_type
      ? normalize_equipment_type_key(source.equipment_type)
      : source.source_id;
    if (!groups.has(type_key)) {
      const meta = equipment_type_labels[type_key];
      const label = meta
        ? `${meta.icon ? `${meta.icon} ` : ""}${meta.name_fi}`
        : type_key;
      groups.set(type_key, { key: type_key, label, source_ids: [] });
      order.push(type_key);
    }
    groups.get(type_key)!.source_ids.push(source.source_id);
  }
  return order.map((key) => groups.get(key)!);
}

/** All instance source ids across the selected type groups, deterministically ordered (group
 * order, then within-group order) and deduplicated. Not capped — the caller applies the
 * scope-aware bounded-overlay cap (MAX_SOURCES). */
export function matching_source_ids(
  type_groups: TypeGroup[],
  selected_type_keys: string[],
): string[] {
  const selected = new Set(selected_type_keys);
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const group of type_groups) {
    if (!selected.has(group.key)) continue;
    for (const id of group.source_ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

function series_key(
  building_id: string,
  source_id: string,
  name: string,
): string {
  return `${building_id}|${source_id}|${name}`;
}

function panel_group_key(
  name: string,
  unit: string,
  split_units: ReadonlySet<string>,
): string {
  const trimmed = unit.trim();
  // Dimensionless / missing units must not overlay — they are not a shared axis.
  if (!trimmed) return `name:${name}`;
  if (split_units.has(trimmed)) return `unit:${trimmed}|name:${name}`;
  return `unit:${trimmed}`;
}

export function group_series_into_panels<T extends TrendSeries>(
  series: T[],
  split_units: ReadonlySet<string> = new Set(),
): { panel_key: string; unit: string; series: T[] }[] {
  const groups = new Map<string, T[]>();
  const order: string[] = [];
  for (const item of series) {
    const key = panel_group_key(item.name, item.unit, split_units);
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(item);
  }
  return order.map((panel_key) => {
    const grouped = groups.get(panel_key)!;
    return {
      panel_key,
      unit: grouped[0]?.unit ?? "",
      series: grouped,
    };
  });
}

export function default_selected_building_ids(
  building_id: string | null,
  candidates: { id: string }[],
): string[] {
  if (building_id) return [building_id];
  return candidates
    .slice(0, DEFAULT_COMPARE_BUILDING_COUNT)
    .map((building) => building.id);
}

/** Every series line is a selected (building × instance × signal-name), never a synthesized
 * per-building representative. The caller (TrendView) is responsible for
 * capping `selected_source_ids` at the scope-aware bounded-overlay cap before calling this —
 * the same capped id list drives both this overlay and the source × building scope, whether one
 * building or a Kiinteistösalkku/-kanta selection. */
export function build_trend_series(opts: {
  selected_building_ids: string[];
  buildings: { id: string; name: string }[];
  sources_by_building: Record<string, SeriesSource[]>;
  selected_source_ids: string[];
  selected_names: string[];
}): TrendSeries[] {
  const building_name = new Map(
    opts.buildings.map((building) => [building.id, building.name]),
  );
  const series: TrendSeries[] = [];
  const selected_source_ids = new Set(opts.selected_source_ids);

  for (const building_id of opts.selected_building_ids) {
    const sources = opts.sources_by_building[building_id] ?? [];
    const name_of = building_name.get(building_id) ?? building_id;
    const selected_sources = sources.filter((source) =>
      selected_source_ids.has(source.source_id),
    );
    for (const source of selected_sources) {
      for (const name of opts.selected_names) {
        if (!source.available_names.some((entry) => entry.name === name)) {
          continue;
        }
        series.push(make_series(building_id, name_of, source, name));
      }
    }
  }
  return series;
}

/** Label for a step-2b Instanssit chip — adapts the chart legend's `building · name` pattern
 * (TrendView `series_legend`) to the instance picker: a salkku/kanta scope (more than one
 * building selected) prefixes the owning building so two same-named instances in different
 * buildings stay distinguishable; a single-building scope already implies the building, so the
 * instance name alone is shown without duplicating it. */
export function instance_chip_label(
  source_name: string,
  building_name: string | undefined,
  multi_building: boolean,
): string {
  if (multi_building && building_name)
    return `${building_name} · ${source_name}`;
  return source_name;
}

function make_series(
  building_id: string,
  building_name: string,
  source: SeriesSource,
  name: string,
): TrendSeries {
  const unit =
    source.available_names.find((entry) => entry.name === name)?.unit ?? "";
  return {
    series_key: series_key(building_id, source.source_id, name),
    building_id,
    building_name,
    source_id: source.source_id,
    source_name: source.source_name,
    name,
    unit,
  };
}
